import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import axios from "axios";
import "@/App.css";
import { API } from "@/constants";
import { Navbar } from "@/components/Navbar";
import { HeroSection } from "@/components/HeroSection";
import { PromoBanner } from "@/components/PromoBanner";
import { RoomsSection } from "@/components/RoomsSection";
import { ScrollToTop } from "@/components/ScrollToTop";

// Below-the-fold sections — code-split so mobile gets less JS upfront.
const AttractionsSection = lazy(() =>
  import("@/components/AttractionsSection").then((m) => ({ default: m.AttractionsSection }))
);
const DiningSection = lazy(() =>
  import("@/components/DiningSection").then((m) => ({ default: m.DiningSection }))
);
const KidsSection = lazy(() =>
  import("@/components/KidsSection").then((m) => ({ default: m.KidsSection }))
);
const AmenitiesSection = lazy(() =>
  import("@/components/AmenitiesSection").then((m) => ({ default: m.AmenitiesSection }))
);
const TestimonialsSection = lazy(() =>
  import("@/components/TestimonialsSection").then((m) => ({ default: m.TestimonialsSection }))
);
const InquirySection = lazy(() =>
  import("@/components/InquirySection").then((m) => ({ default: m.InquirySection }))
);
const BlogSection = lazy(() =>
  import("@/components/BlogSection").then((m) => ({ default: m.BlogSection }))
);
const ContactSection = lazy(() =>
  import("@/components/ContactSection").then((m) => ({ default: m.ContactSection }))
);
const Footer = lazy(() =>
  import("@/components/Footer").then((m) => ({ default: m.Footer }))
);
const FaqSection = lazy(() =>
  import("@/components/FaqSection").then((m) => ({ default: m.FaqSection }))
);
const OffersSection = lazy(() =>
  import("@/components/OffersSection").then((m) => ({ default: m.OffersSection }))
);
const GallerySection = lazy(() =>
  import("@/components/GallerySection").then((m) => ({ default: m.GallerySection }))
);
const BookingWidget = lazy(() =>
  import("@/components/BookingWidget").then((m) => ({ default: m.BookingWidget }))
);
const AdminApp = lazy(() => import("@/components/AdminApp"));
const FeedbackApp = lazy(() => import("@/components/FeedbackApp"));

function SiteApp() {
  const [attractions, setAttractions] = useState([]);
  const [dining, setDining] = useState([]);
  const [kids, setKids] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [aRes, dRes, kRes, tRes, rRes, cRes] = await Promise.all([
        axios.get(`${API}/attractions`),
        axios.get(`${API}/dining`),
        axios.get(`${API}/kids`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/rooms`),
        axios.get(`${API}/contact`),
      ]);
      setAttractions(aRes.data);
      setDining(dRes.data);
      setKids(kRes.data);
      setTestimonials(tRes.data);
      setRooms(rRes.data);
      setContact(cRes.data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#F8F9FA]"
        data-testid="loading-screen"
      >
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0" data-testid="app-root">
      <Navbar />
      <HeroSection />
      <PromoBanner />
      {rooms.length > 0 && <RoomsSection rooms={rooms} />}
      <Suspense fallback={null}>
        <GallerySection />
        <OffersSection />
        {attractions.length > 0 && (
          <AttractionsSection attractions={attractions} />
        )}
        {dining.length > 0 && <DiningSection dining={dining} />}
        {kids.length > 0 && <KidsSection kids={kids} />}
        <AmenitiesSection />
        {testimonials.length > 0 && (
          <TestimonialsSection testimonials={testimonials} />
        )}
        <InquirySection />
        <BlogSection />
        <FaqSection />
        {contact && <ContactSection contact={contact} />}
        <Footer />
        <BookingWidget />
      </Suspense>
      <ScrollToTop />
    </div>
  );
}

// Hook-free router: /admin and /feedback render standalone pages, everything else the site.
function App() {
  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    if (path.startsWith("/admin")) {
      return (
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center bg-[#0f1e3d]" />
          }
        >
          <AdminApp />
        </Suspense>
      );
    }
    if (path.startsWith("/feedback")) {
      return (
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center bg-[#0f1e3d]" />
          }
        >
          <FeedbackApp />
        </Suspense>
      );
    }
  }
  return <SiteApp />;
}

export default App;
